import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createDraft } from 'immer'
import { Theme } from '@mui/system'
import { setElementVars } from '@vanilla-extract/dynamic'

import { useApiStore } from '@/stores/app/api'
import { defaultTheme, hexToHsl, themeClient } from '@/utils/app/theme'
import { api } from '@/utils/constants'
import { primary, secondary } from '@/theme/defaultPalette.css'

import { ManagerBinding } from '@/types/park/manager'
import {
    ParametersManager,
    ParametersManagerRequest,
} from '@/types/shared/manager'

import logo from 'assets/images/Logo_Konvives.png'

type ThemeStore = {
    logo: string
    theme: Theme
    manager: ManagerBinding
    hasOrder: boolean
    initialiseTheme: () => void
    updateTheme: (clientName: string) => Promise<void>
}

export const useThemeStore = create<ThemeStore>()(
    immer((set) => ({
        // TODO: Replace with logo: assets.eThik.LOGO,
        logo,

        theme: defaultTheme,

        manager: {
            id: -1,
            name: '',
        },

        hasOrder: false,

        initialiseTheme: () => {
            const htmlElement = document.querySelector('html')

            if (!htmlElement) {
                return
            }

            setElementVars(htmlElement, {
                [primary.LIGHT]: '240, 40%, 40%',
                [primary.MAIN]: '240, 40%, 25%',
                [primary.DARK]: '240, 40%, 10%',

                [secondary.LIGHT]: '105, 35%, 50%',
                [secondary.MAIN]: '105, 35%, 35%',
                [secondary.DARK]: '105, 35%, 20%',
            })
        },

        updateTheme: async (clientName) => {
            const { requestWithoutHooks } = useApiStore.getState()

            if (clientName === 'auth') {
                return
            }

            const requestSettings = await requestWithoutHooks
                .get(api.settings.THEME, {
                    searchParams: {
                        client__name: decodeURIComponent(clientName),
                    },
                })
                .json<ParametersManagerRequest>()
                .catch(() => {
                    /*Empty*/
                })

            if (!requestSettings || requestSettings.results.length === 0) {
                return
            }

            const [userSettings]: ParametersManager[] = requestSettings.results

            const htmlElement = document.querySelector('html')

            if (!htmlElement) {
                return
            }

            const primaryHsl = hexToHsl(userSettings.primary)
            const secondaryHsl = hexToHsl(userSettings.secondary)

            const plLight = Math.min(primaryHsl[2] + 15, 100)
            const plDark = Math.max(primaryHsl[2] - 15, 0)

            const slLight = Math.min(secondaryHsl[2] + 15, 100)
            const slDark = Math.max(secondaryHsl[2] - 15, 0)

            setElementVars(htmlElement, {
                [primary.LIGHT]: `${primaryHsl[0]}, ${primaryHsl[1]}%, ${plLight}%`,
                [primary.MAIN]: `${primaryHsl[0]}, ${primaryHsl[1]}%, ${primaryHsl[2]}%`,
                [primary.DARK]: `${primaryHsl[0]}, ${primaryHsl[1]}%, ${plDark}%`,

                [secondary.LIGHT]: `${secondaryHsl[0]}, ${secondaryHsl[1]}%, ${slLight}%`,
                [secondary.MAIN]: `${secondaryHsl[0]}, ${secondaryHsl[1]}%, ${secondaryHsl[2]}%`,
                [secondary.DARK]: `${secondaryHsl[0]}, ${secondaryHsl[1]}%, ${slDark}%`,
            })

            set((state) => {
                state.logo = userSettings.logo_konvive
                state.manager = userSettings.manager
                state.hasOrder = userSettings.has_order

                state.theme = createDraft(
                    themeClient({
                        primary: userSettings.primary,
                        secondary: userSettings.secondary,
                    }),
                )
            })
        },
    })),
)
